import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  TextInput,
  SelectDropdown,
  RequiredFieldIndicator,
} from '@gsa/afp-component-library';
import OverlaySpinner from '../../../../components/overlay-spinner';
import { useBidLineDetails } from '../provider/bid-line-details-provider';
import { INPUT_DROPDOWN_DEFAULT_OPTION, COLOR_TYPE } from '../provider/helpers';
import {
  CREATE_BID_LINE_COLOR,
  UPDATE_BID_LINE_COLOR,
} from '../provider/queries';
import CanIChangeBidData from '../../../bids/components/protect-bid-data-crud';
import { pageErrorAlert } from '../../../../utilities/error-handling';

const AddColor = ({ selectedColor, showEdit, setShowEdit }) => {
  const { bidLine, colors, setColors, gsaColors, setAlert, isAdmin } =
    useBidLineDetails();
  const colorNameRef = useRef(null);

  const requiredText = 'This is a required field';
  const schema = yup.object().shape({
    vendorColorName: yup
      .string()
      .required(requiredText)
      .matches(
        /[a-zA-Z0-9]+/,
        'Must contain at least one alphanumeric character',
      ),
    vendorColorCode: yup
      .string()
      .required(requiredText)
      .matches(
        /^[a-zA-Z0-9 _\-/]+$/,
        'Special characters not allowed except "-", "_", "/" or space',
      )
      .matches(
        /[a-zA-Z0-9]+/,
        'Must contain at least one alphanumeric character',
      )
      .test('color-exists', 'Color code already exists', function (code) {
        if (showEdit) return true; // skip this validation if editing existing color
        // eslint-disable-next-line react/no-this-in-sfc
        const { existingColors } = this.parent;
        return !existingColors.some(
          (c) => c.vendorColorCode.toUpperCase() === code.toUpperCase(),
        );
      }),
    gsaColorCodeId: yup.string().required(requiredText),
    colorType: yup.string().required(requiredText),
    price: yup
      .number()
      .transform((value, originalValue) => {
        // Convert empty string to undefined so Yup skips number validation
        return originalValue === '' ? undefined : value;
      })
      .when('colorType', {
        is: (colorType) => {
          return colorType === COLOR_TYPE.optional;
        },
        then: yup
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .integer('Must be a whole number')
          .typeError(requiredText)
          .min(1, 'Must be $1 or more')
          .max(9999, 'Must be less than $10,000')
          .required(requiredText),
        // If colorType is not optional, we don't require price and allow null values
        otherwise: yup
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .notRequired()
          .nullable(),
      }),
  });

  const {
    formState: { errors },
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      vendorColorName: '',
      vendorColorCode: '',
      gsaColorCodeId: '',
      colorType: '',
      price: '',
      existingColors: colors,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const colorType = watch('colorType');

  useEffect(() => {
    setValue('existingColors', colors);
  }, [colors]);

  useEffect(() => {
    if (showEdit && selectedColor) {
      clearErrors();
      setValue('vendorColorName', selectedColor.vendorColorName);
      setValue('vendorColorCode', selectedColor.vendorColorCode);
      setValue('gsaColorCodeId', selectedColor.gsaColorCodeId);
      setValue('colorType', selectedColor.standardPremiumCode);
      setValue('price', selectedColor.price);
      colorNameRef.current.focus();
    } else {
      reset();
    }
  }, [showEdit, selectedColor]);

  const [createColor, { loading }] = useMutation(CREATE_BID_LINE_COLOR, {
    onError: (error) => {
      setAlert(pageErrorAlert(error, `Unable to add color: ${error.message}`));
    },
    onCompleted: ({ createBidLineColor }) => {
      reset(); // reset form
      setColors((prevColors) => [...prevColors, createBidLineColor]);
      setAlert({
        type: 'success',
        message: (
          <>
            Color <b>{createBidLineColor.vendorColorName}</b> has been added
            successfully
          </>
        ),
      });
    },
  });

  const [updateColor, { loading: updateLoading }] = useMutation(
    UPDATE_BID_LINE_COLOR,
    {
      onError: (error) => {
        setAlert(
          pageErrorAlert(error, `Unable to update color: ${error.message}`),
        );
      },
      onCompleted: ({ updateBidLineColor }) => {
        setShowEdit(false);
        setColors((prevColors) =>
          prevColors.map((color) =>
            color.id === updateBidLineColor.id ? updateBidLineColor : color,
          ),
        );
        setAlert({
          type: 'success',
          message: (
            <>
              Color <b>{updateBidLineColor.vendorColorName}</b> has been updated
              successfully
            </>
          ),
        });
      },
    },
  );

  const gsaColorOptions = [INPUT_DROPDOWN_DEFAULT_OPTION];
  gsaColors.forEach((color) =>
    gsaColorOptions.push({ label: color.title, value: color.id }),
  );

  const colorTypeOptions = [
    INPUT_DROPDOWN_DEFAULT_OPTION,
    { label: 'Standard', value: COLOR_TYPE.standard },
    { label: 'Optional', value: COLOR_TYPE.optional },
  ];

  const onSubmit = (data) => {
    setAlert(null);
    if (isAdmin) return; // skip API call for site admin, CS, CO, engineer

    if (showEdit) {
      const updatedFields = {};
      if (data.vendorColorName !== selectedColor.vendorColorName) {
        updatedFields.vendorColorName = data.vendorColorName;
      }
      if (+data.gsaColorCodeId !== selectedColor.gsaColorCodeId) {
        updatedFields.gsaColorCodeId = +data.gsaColorCodeId;
      }
      if (data.colorType !== selectedColor.standardPremiumCode) {
        updatedFields.standardPremiumCode = data.colorType;
      }
      if (+data.price !== selectedColor.price) {
        updatedFields.price = +data.price;
      }
      if (Object.keys(updatedFields).length > 0) {
        updateColor({
          variables: {
            input: {
              bidLineColorId: selectedColor.id,
              ...updatedFields,
            },
          },
        });
      }
    } else {
      createColor({
        variables: {
          input: {
            bidLineId: +bidLine.id,
            vendorColorName: data.vendorColorName,
            vendorColorCode: data.vendorColorCode,
            gsaColorCodeId: +data.gsaColorCodeId,
            standardPremiumCode: data.colorType,
            price: data.price,
          },
        },
      });
    }
  };

  const genLabel = (label, required) => (
    <span className="text-bold">
      {label} {required && <RequiredFieldIndicator />}
    </span>
  );

  return (
    <div className="bg-primary-lightest padding-bottom-2">
      {(loading || updateLoading) && <OverlaySpinner />}
      <form
        id="commenting-period-schedule-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="existingColors"
          control={control}
          render={() => null}
        />
        <div className="display-flex flex-row flex-justify flex-align-end margin-x-2">
          <div
            className={`grid-col-2 ${
              errors.vendorColorCode ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="vendorColorCode"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <TextInput
                  name={name}
                  data-testid="add-color-vendor-color-code"
                  type="text"
                  label={genLabel('Vendor color code', true)}
                  maxLength={10}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.vendorColorCode?.message}
                  disabled={showEdit}
                />
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${
              errors.vendorColorName ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="vendorColorName"
              control={control}
              defaultValue=""
              render={({ field: { name, value, onChange, onBlur } }) => (
                <TextInput
                  name={name}
                  data-testid="add-color-vendor-color-name"
                  type="text"
                  label={genLabel('Vendor color name', true)}
                  maxLength={30}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.vendorColorName?.message}
                  inputRef={colorNameRef}
                />
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${
              errors.gsaColorCodeId ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="gsaColorCodeId"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <SelectDropdown
                  name={name}
                  id="add-color-gsa-color"
                  data-testid="add-color-gsa-color"
                  label={genLabel('GSA color', true)}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={errors.gsaColorCodeId?.message}
                  options={gsaColorOptions}
                />
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${errors.colorType ? 'padding-left-2' : ''}`}
          >
            <Controller
              name="colorType"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <SelectDropdown
                  name={name}
                  id="add-color-is-optional"
                  data-testid="add-color-standard-optional"
                  label={genLabel('Standard or optional', true)}
                  value={value}
                  onChange={(evt) => {
                    onChange(evt);
                    if (evt.target.value !== COLOR_TYPE.optional) {
                      setValue('price', ''); // reset price if not optional
                    }
                  }}
                  onBlur={onBlur}
                  errorMessage={errors.colorType?.message}
                  options={colorTypeOptions}
                />
              )}
            />
          </div>
          <div className={`grid-col-2 ${errors.price ? 'padding-left-2' : ''}`}>
            {colorType === 'Premium' && (
              <Controller
                name="price"
                control={control}
                render={({ field: { name, value, onChange, onBlur } }) => (
                  <TextInput
                    name={name}
                    data-testid="add-color-price"
                    type="number"
                    prefix="$"
                    label={genLabel('Price', true)}
                    maxLength={10}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.price?.message}
                  />
                )}
              />
            )}
          </div>
          <div className="text-right">
            <CanIChangeBidData>
              {!showEdit && (
                <Button
                  type="submit"
                  variant="outline"
                  label="Add"
                  leftIcon={{ name: 'add' }}
                  className="margin-left-2 margin-right-0"
                />
              )}
              {showEdit && (
                <Button
                  type="submit"
                  variant="outline"
                  label="Save"
                  className="margin-left-2 margin-right-1"
                />
              )}
              {showEdit && (
                <Button
                  type="button"
                  variant="unstyled"
                  label="Cancel"
                  className="margin-right-0"
                  onClick={() => {
                    setShowEdit(false);
                    setAlert(null);
                  }}
                />
              )}
            </CanIChangeBidData>
          </div>
        </div>
      </form>
    </div>
  );
};

AddColor.propTypes = {
  showEdit: PropTypes.bool.isRequired,
  setShowEdit: PropTypes.func.isRequired,
  selectedColor: PropTypes.shape({
    id: PropTypes.number,
    vendorColorCode: PropTypes.string,
    vendorColorName: PropTypes.string,
    gsaColorCodeId: PropTypes.number,
    standardPremiumCode: PropTypes.string,
    price: PropTypes.number,
  }),
};

export default AddColor;
